import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "railsui.colorScheme"
const DARK_MODE = "dark"
const LIGHT_MODE = "light"
const SYSTEM_QUERY = "(prefers-color-scheme: dark)"

export default class extends Controller {
  static targets = ["toggle"]

  connect() {
    this.mediaQuery = this.matchMedia()
    this.handleSystemChange = this.handleSystemChange.bind(this)
    this.handleStorage = this.handleStorage.bind(this)

    this.applyResolvedMode()
    this.addSystemListener()
    window.addEventListener("storage", this.handleStorage)
  }

  disconnect() {
    this.removeSystemListener()
    window.removeEventListener("storage", this.handleStorage)
  }

  toggle(event) {
    const mode = event.currentTarget.checked ? DARK_MODE : LIGHT_MODE

    this.writeStoredMode(mode)
    this.applyMode(mode)
  }

  // Sets an explicit mode from a discrete control (e.g. a flyout option),
  // rather than the boolean checkbox. "system" clears the stored preference
  // and falls back to the OS setting.
  setMode(event) {
    event.preventDefault()
    const mode = event.params.mode

    if (mode === "system") {
      this.clearStoredMode()
      this.applySystemMode(this.systemPrefersDark() ? DARK_MODE : LIGHT_MODE)
    } else if (mode === DARK_MODE || mode === LIGHT_MODE) {
      this.writeStoredMode(mode)
      this.applyMode(mode)
    }
  }

  clearStoredMode() {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      // Ignore storage failures so private browsing still gets an in-page toggle.
    }
  }

  handleSystemChange(event) {
    if (this.storedMode()) return

    const isDark = typeof event?.matches === "boolean" ? event.matches : this.systemPrefersDark()

    this.applySystemMode(isDark ? DARK_MODE : LIGHT_MODE)
  }

  handleStorage(event) {
    if (event.key && event.key !== STORAGE_KEY) return

    this.applyResolvedMode()
  }

  applyResolvedMode() {
    const storedMode = this.storedMode()

    if (storedMode) {
      this.applyMode(storedMode)
    } else {
      this.applySystemMode(this.resolvedMode())
    }
  }

  resolvedMode() {
    return this.storedMode() || (this.systemPrefersDark() ? DARK_MODE : LIGHT_MODE)
  }

  storedMode() {
    const mode = this.readStoredMode()

    return mode === DARK_MODE || mode === LIGHT_MODE ? mode : null
  }

  readStoredMode() {
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch (error) {
      return null
    }
  }

  writeStoredMode(mode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode)
    } catch (error) {
      // Ignore storage failures so private browsing still gets an in-page toggle.
    }
  }

  applyMode(mode) {
    this.applyTheme(mode, "stored")
    window.dispatchEvent(new CustomEvent("railsui:color-scheme-change", { detail: { mode } }))
  }

  applySystemMode(mode) {
    this.applyTheme(mode, "system")
    window.dispatchEvent(new CustomEvent("railsui:color-scheme-change", { detail: { mode, source: "system" } }))
  }

  applyTheme(mode, source) {
    const isDark = mode === DARK_MODE

    document.documentElement.dataset.theme = mode
    document.documentElement.dataset.themeSource = source
    document.documentElement.style.colorScheme = mode
    this.syncToggleTargets(isDark)
  }

  syncToggleTargets(isDark) {
    this.toggleTargets.forEach((toggle) => {
      toggle.checked = isDark
      toggle.setAttribute("aria-checked", isDark.toString())
    })
  }

  systemPrefersDark() {
    return Boolean(this.mediaQuery?.matches)
  }

  matchMedia() {
    return window.matchMedia ? window.matchMedia(SYSTEM_QUERY) : null
  }

  addSystemListener() {
    if (!this.mediaQuery) return

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.handleSystemChange)
    } else if (this.mediaQuery.addListener) {
      this.mediaQuery.addListener(this.handleSystemChange)
    }
  }

  removeSystemListener() {
    if (!this.mediaQuery) return

    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener("change", this.handleSystemChange)
    } else if (this.mediaQuery.removeListener) {
      this.mediaQuery.removeListener(this.handleSystemChange)
    }
  }
}
